import { useCallback, useEffect, useRef, useState } from "react";
import Segmented from "@/components/Segmented";
import { CheckCircle2, Download, FileText, History, Loader2, Search, Trash2,
         Upload, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { opsApi } from "../lib/api";

const KB = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`);

/** OPS-7 — every project file, in a folder that other clusters file into automatically. */
export default function DocumentsTab({ project, reload }) {
  const [state, setState] = useState(null);
  const [folder, setFolder] = useState("contract");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [versions, setVersions] = useState(null);
  const [tagInput, setTagInput] = useState("");
  const [specInput, setSpecInput] = useState("");
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    setState(await opsApi.listDocuments(project.id, { folder, search }));
  }, [project.id, folder, search]);

  useEffect(() => { const t = setTimeout(load, search ? 250 : 0); return () => clearTimeout(t); },
    [load, search]);

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const res = await opsApi.uploadDocuments(project.id, Array.from(files), {
        folder, tags: tagInput, spec_section: folder === "drawings_specs" ? specInput : "",
      });
      if (res.saved.length) toast.success(`${res.saved.length} file(s) filed under ${folder.replace("_", " ")}`);
      (res.failed || []).forEach((f) => toast.error(`${f.file_name}: ${f.error}`));
      setTagInput(""); setSpecInput("");
      await load();
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const remove = async (doc) => {
    await opsApi.deleteDocument(doc.id);
    toast.success("Removed");
    await load();
    await reload();
  };

  if (!state) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading documents…
      </div>
    );
  }

  const checklist = project.checklist || {};

  return (
    <div className="space-y-4" data-testid="ops-documents-tab">
      {checklist.items?.length > 0 && (
        <div className="flex flex-wrap gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3"
             data-testid="ops-documents-checklist">
          {checklist.items.map((i) => (
            <span key={i.key} className={`inline-flex items-center gap-1.5 text-xs ${
              i.satisfied ? "text-emerald-700" : "text-amber-700"}`}>
              {i.satisfied ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
              {i.label}
            </span>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Segmented
          options={(state.folders || []).map((f) => ({ key: f.key, label: f.label,
                                                       count: state.counts?.[f.key] || undefined,
                                                       testId: `ops-folder-${f.key}` }))}
          value={folder}
          onChange={setFolder}
        />
      </div>

      <div className={`rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors ${
        dragging ? "border-slate-900 bg-slate-50" : "border-slate-200"}`}
           onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
           onDragLeave={() => setDragging(false)}
           onDrop={(e) => { e.preventDefault(); setDragging(false); upload(e.dataTransfer.files); }}
           data-testid="ops-documents-dropzone">
        <input ref={inputRef} type="file" multiple hidden data-testid="ops-documents-file-input"
               onChange={(e) => upload(e.target.files)} />
        <p className="text-sm text-slate-600">
          Drop files here, or{" "}
          <button className="font-medium text-blue-600 hover:underline"
                  onClick={() => inputRef.current?.click()} data-testid="ops-documents-browse">
            choose files
          </button>{" "}
          to file them under <span className="font-medium">{state.folders.find((f) => f.key === folder)?.label}</span>
        </p>
        <div className="mx-auto mt-3 flex max-w-xl flex-wrap items-center justify-center gap-2">
          <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                 placeholder="Tags, comma separated (e.g. executed-contract)"
                 className="h-8 w-64 text-xs" data-testid="ops-documents-tags" />
          {folder === "drawings_specs" && (
            <Input value={specInput} onChange={(e) => setSpecInput(e.target.value)}
                   placeholder="Spec section (e.g. 10 28 00)"
                   className="h-8 w-52 text-xs" data-testid="ops-documents-spec-section" />
          )}
          {busy && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
        </div>
        <p className="mt-2 text-[11px] text-slate-400">Up to 20 files, 500MB each. A file with the same
          name becomes a new version — the old one is kept.</p>
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="File name, tag or spec section"
                 className="h-9 w-72 pl-8 text-sm" data-testid="ops-documents-search" />
        </div>
        <span className="text-xs text-slate-500">{state.documents.length} file(s)</span>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        {state.documents.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-12 text-center">
            <FileText className="h-7 w-7 text-slate-300" />
            <p className="text-sm text-slate-600">Nothing filed here yet</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">File</th>
                <th className="px-3 py-2 text-left">Tags</th>
                <th className="px-3 py-2 text-left">Expires</th>
                <th className="px-3 py-2 text-left">Uploaded</th>
                <th className="px-3 py-2 text-right">Size</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {state.documents.map((d) => (
                <tr key={d.id} data-testid={`ops-document-row-${d.id}`}>
                  <td className="px-4 py-2">
                    <a href="#" onClick={(e) => { e.preventDefault(); opsApi.openDocument(d.id); }}
                       className="font-medium text-slate-900 hover:text-blue-600"
                       data-testid={`ops-document-open-${d.id}`}>
                      {d.file_name}
                    </a>
                    <span className="ml-2 text-xs text-slate-400">v{d.version}</span>
                    {d.spec_section && <span className="ml-2 text-xs text-slate-500">spec {d.spec_section}</span>}
                    {d.source_record?.kind && (
                      <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase text-slate-500">
                        {d.source_record.kind}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">{(d.tags || []).join(", ") || "—"}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">{d.expires_on || "—"}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">
                    {d.uploaded_by}<br />{new Date(d.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-3 py-2 text-right text-xs text-slate-500">{KB(d.size)}</td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      <a href="#" onClick={(e) => { e.preventDefault(); opsApi.downloadDocument(d.id, d.file_name); }}
                         className="rounded p-1 text-slate-400 hover:text-slate-800"
                         data-testid={`ops-document-download-${d.id}`}>
                        <Download className="h-3.5 w-3.5" />
                      </a>
                      <button onClick={async () => setVersions(await opsApi.documentVersions(d.id))}
                              className="rounded p-1 text-slate-400 hover:text-slate-800"
                              data-testid={`ops-document-versions-${d.id}`}>
                        <History className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => remove(d)}
                              className="rounded p-1 text-slate-400 hover:text-rose-600"
                              data-testid={`ops-document-delete-${d.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {versions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
             onClick={() => setVersions(null)}>
          <div className="w-full max-w-md rounded-xl bg-white p-5" onClick={(e) => e.stopPropagation()}
               data-testid="ops-document-versions-dialog">
            <h3 className="text-base font-semibold text-slate-900">{versions.file_name}</h3>
            <ul className="mt-3 space-y-2">
              {versions.versions.map((v) => (
                <li key={v.id} className="flex items-center justify-between text-sm">
                  <a href="#" onClick={(e) => { e.preventDefault(); opsApi.openDocument(v.id); }}
                     className="text-blue-600 hover:underline">Version {v.version}</a>
                  <span className="text-xs text-slate-500">
                    {v.uploaded_by} · {new Date(v.created_at).toLocaleString()}
                    {v.is_latest ? " · latest" : ""}
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-4 flex justify-end">
              <Button size="sm" variant="ghost" onClick={() => setVersions(null)}
                      data-testid="ops-document-versions-close">Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
